import React, { useState, useEffect } from 'react';
import { X, User, Mail, MapPin, Briefcase, DollarSign, Award, Globe, Shield, FileText, AlertCircle, Star, CheckCircle, ExternalLink, Clock } from 'lucide-react';
import { candidatesAPI } from "../../services/api";
import { toast } from "react-toastify";

// Helper function to format field name
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Get category from field name
const getCategoryFromFieldName = (fieldName) => {
  const lowerField = fieldName.toLowerCase();
  
  if (lowerField.includes('name') || lowerField.includes('dob') || 
      lowerField.includes('date') && lowerField.includes('birth') || 
      lowerField === 'gender' || lowerField === 'age' || lowerField.includes('marital')) {
    return 'Personal';
  }
  
  if (lowerField.includes('email') || lowerField.includes('phone') || 
      lowerField.includes('mobile') || lowerField.includes('whatsapp') ||
      lowerField.includes('linkedin') || lowerField.includes('github')) {
    return 'Contact';
  }
  
  if (lowerField.includes('address') || lowerField === 'city' || 
      lowerField === 'state' || lowerField === 'country' || 
      lowerField.includes('postal') || lowerField === 'province' ||
      lowerField === 'region') {
    return 'Address';
  }
  
  if (lowerField.includes('currentjob') || lowerField.includes('currentcompany') || 
      lowerField.includes('previousjob') || lowerField.includes('previousexp') ||
      lowerField.includes('experience') || lowerField.includes('title') ||
      lowerField.includes('employment') || lowerField.includes('notice') ||
      lowerField.includes('team') || lowerField.includes('career')) {
    return 'Employment';
  }
  
  if (lowerField.includes('ctc') || lowerField.includes('salary') || 
      lowerField.includes('compensation') || lowerField.includes('currency') ||
      lowerField.includes('frequency') || lowerField.includes('given') ||
      lowerField.includes('joining') && lowerField.includes('date')) {
    return 'Compensation';
  }
  
  if (lowerField.includes('education') || lowerField.includes('university') || 
      lowerField.includes('degree') || lowerField.includes('gpa') ||
      lowerField.includes('field') && lowerField.includes('study')) {
    return 'Education';
  }
  
  if (lowerField.includes('visa') || lowerField.includes('work') && lowerField.includes('auth') ||
      lowerField.includes('citizen')) {
    return 'Work Authorization';
  }
  
  if (lowerField.includes('reference')) {
    return 'References';
  }
  
  if (lowerField.includes('consent') || lowerField.includes('agree') || 
      lowerField.includes('authorization') || lowerField.includes('permission') ||
      lowerField.includes('confirm') || lowerField.includes('verification')) {
    return 'Consents';
  }
  
  if (lowerField.includes('relocate') || lowerField.includes('relocation') ||
      lowerField === 'currentlocation') {
    return 'Location';
  }
  
  if (lowerField.includes('source') || lowerField.includes('referral')) {
    return 'Other';
  }
  
  return 'Other';
};

// Get icon for category
const getCategoryIcon = (category) => {
  const iconMap = {
    'Personal': <User size={20} className="text-blue-600" />,
    'Contact': <Mail size={20} className="text-green-600" />,
    'Address': <MapPin size={20} className="text-red-600" />,
    'Employment': <Briefcase size={20} className="text-indigo-600" />,
    'Compensation': <DollarSign size={20} className="text-green-600" />,
    'Education': <Award size={20} className="text-teal-600" />,
    'Work Authorization': <Shield size={20} className="text-orange-600" />,
    'References': <User size={20} className="text-cyan-600" />,
    'Consents': <Shield size={20} className="text-purple-600" />,
    'Skills': <Award size={20} className="text-yellow-600" />,
    'Location': <Globe size={20} className="text-orange-600" />,
    'Documents': <FileText size={20} className="text-purple-600" />,
    'Screening Questions': <AlertCircle size={20} className="text-indigo-600" />,
  };
  
  return iconMap[category] || <FileText size={20} className="text-gray-600" />;
};

const CandidatePreview = ({ candidate: initialCandidate, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [candidateData, setCandidateData] = useState(initialCandidate);

  // Fields that should NOT be rendered in the regular fields section
  const EXCLUDED_FIELDS = [
    'technicalSkills', 'softSkills', 'languages', 'preferredLocations', 
    'keyAchievements', 'awardsRecognition', 'coverLetterUrl', 
    'profilePhotoUrl', 'certificationsUpload', 'supportingDocuments',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'id', 'organizationId',
    'jobIds', 'applicationId', 'clientIds', 'status', 'screeningQuestionsByJob',
    'professionalLicenses', 'areaProfessionalCertifications', 'resumeUrl',
    'stageHistory', 'pipelineStage', 'reason', 'timeslots',
    'selectedSlot', 'meetLink', 'clientTimeZone', 'userTimeZone',
    'statusLockedByClient', 'statusSetBy'
  ];

  // Load complete candidate data and field configurations
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        console.log("🔍 Loading candidate data for ID:", initialCandidate.id);
        
        // ✅ FETCH COMPLETE CANDIDATE DATA FROM API
        let fullCandidateData = initialCandidate;
        
        if (initialCandidate.id) {
          try {
            console.log("📥 Fetching full candidate data from API...");
            const candidateResponse = await candidatesAPI.getApplicationById(initialCandidate.id);
            console.log("✅ Full candidate data received:", candidateResponse.data);
            fullCandidateData = candidateResponse.data || initialCandidate;
            setCandidateData(fullCandidateData);
          } catch (error) {
            console.warn("⚠️ Could not fetch full candidate data, using initial data:", error);
            fullCandidateData = initialCandidate;
          }
        }

        // ✅ FETCH FIELD CONFIGURATIONS
        console.log("📥 Fetching field configurations...");
        const response = await candidatesAPI.getFields();
        console.log("✅ Field configs received:", response.data);

        let configs = [];

        // Parse field configurations
        if (Array.isArray(response.data)) {
          configs = response.data.map((fieldItem, index) => {
            // Handle both string and object formats
            const fieldName = typeof fieldItem === 'string' 
              ? fieldItem 
              : (fieldItem.fieldName || fieldItem.name || "");
            
            return {
              fieldName: fieldName,
              displayName: typeof fieldItem === 'object' 
                ? (fieldItem.displayName || formatFieldName(fieldName))
                : formatFieldName(fieldName),
              category: typeof fieldItem === 'object'
                ? (fieldItem.category || getCategoryFromFieldName(fieldName))
                : getCategoryFromFieldName(fieldName),
              orderIndex: typeof fieldItem === 'object' 
                ? (fieldItem.orderIndex ?? index)
                : index,
              isRequired: typeof fieldItem === 'object' ? (fieldItem.isRequired || false) : false
            };
          });
        }

        // Remove excluded fields
        configs = configs.filter(field => field.fieldName && !EXCLUDED_FIELDS.includes(field.fieldName));

        // Sort by orderIndex
        configs.sort((a, b) => a.orderIndex - b.orderIndex);
        
        console.log(`✅ Loaded ${configs.length} field configurations`);
        setFieldConfigs(configs);

      } catch (error) {
        console.error("❌ Error loading data:", error);
        toast.error("Failed to load candidate details");
        setFieldConfigs([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [initialCandidate.id]);

  const handleDownload = async (url, filename) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(url, '_blank');
    }
  };

  // Group fields by category
  const getFieldsByCategory = (category) => {
    return fieldConfigs.filter(field => {
      const fieldCategory = field.category || 'Other';
      return fieldCategory === category;
    });
  };

  // Get unique categories in order
  const getCategories = () => {
    const allCategories = fieldConfigs.map(field => field.category || 'Other');
    const uniqueCategories = [...new Set(allCategories)];
    
    const categoryOrder = [
      'Personal', 'Contact', 'Address', 'Employment', 'Compensation', 
      'Education', 'Work Authorization', 'Skills', 'Languages', 
      'Location', 'References', 'Consents', 'Documents', 'Other'
    ];
    
    return uniqueCategories.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  };

  // Format value for display
  const formatValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') return '-';
    
    // Format dates
    if (fieldName.toLowerCase().includes('date') || fieldName === 'dob' || fieldName === 'dateOfBirth') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        return value;
      } catch {
        return value;
      }
    }
    
    // Format boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Format arrays
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return value;
  };

  // ✅ Extract screening questions grouped by job
  const getScreeningQuestions = () => {
    if (!candidateData.screeningQuestionsByJob) return null;
    
    const screeningData = candidateData.screeningQuestionsByJob;
    console.log("📋 Screening questions data:", screeningData);
    
    return screeningData;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
        <div className="bg-white shadow-2xl w-full max-w-3xl h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900">Loading Candidate Details...</h3>
            <p className="text-sm text-gray-500 mt-2">Fetching complete application data</p>
          </div>
        </div>
      </div>
    );
  }

  const screeningQuestions = getScreeningQuestions();

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col animate-slideInRight">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Candidate Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              Application ID: {candidateData.applicationId || 'N/A'}
            </p>
            {candidateData.primaryEmail && (
              <p className="text-sm text-blue-600 mt-1">
                📧 {candidateData.primaryEmail}
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {fieldConfigs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Fields Configured</h3>
              <p className="text-gray-600">No field configurations found.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Status Badge */}
              {candidateData.status && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-shrink-0">
                    <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Current Status</p>
                    <p className="font-semibold text-blue-900">{candidateData.status}</p>
                  </div>
                </div>
              )}

              {/* ✅ Resume URL Section */}
              {candidateData.resumeUrl && (
                <section className="space-y-4 bg-green-50 p-6 rounded-lg border-2 border-green-200">
                  <div className="flex items-center gap-2 pb-2 border-b border-green-300">
                    <FileText size={20} className="text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Resume / CV</h3>
                  </div>
                  
                  <div className="flex gap-3">
                    <a
                      href={candidateData.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center gap-3 p-4 bg-white rounded-lg border border-green-300 hover:bg-green-50 hover:border-green-400 transition-all group"
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <FileText size={24} className="text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                          View Resume
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">Opens in new tab</p>
                      </div>
                      <ExternalLink size={20} className="text-green-600 group-hover:text-green-700 transition-colors" />
                    </a>

                    <button
                      onClick={() => handleDownload(candidateData.resumeUrl, `${candidateData.firstName}_${candidateData.lastName}_Resume.pdf`)}
                      className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-300 hover:bg-green-50 hover:border-green-400 transition-all group"
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <FileText size={24} className="text-green-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                          Download
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">Save to device</p>
                      </div>
                    </button>
                  </div>
                </section>
              )}

              {/* ✅ Cover Letter URL Section */}
              {candidateData.coverLetterUrl && (
                <section className="space-y-4 bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
                  <div className="flex items-center gap-2 pb-2 border-b border-purple-300">
                    <FileText size={20} className="text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Cover Letter</h3>
                  </div>
                  
                  <a
                    href={candidateData.coverLetterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-white rounded-lg border border-purple-300 hover:bg-purple-50 hover:border-purple-400 transition-all group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <FileText size={24} className="text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                        Click to view cover letter in new tab
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">Opens in a new browser tab</p>
                    </div>
                    <ExternalLink size={20} className="text-purple-600 group-hover:text-purple-700 transition-colors" />
                  </a>
                </section>
              )}

              {/* ✅ Screening Questions Section */}
              {screeningQuestions && Object.keys(screeningQuestions).length > 0 && (
                <section className="space-y-4 bg-indigo-50 p-6 rounded-lg border-2 border-indigo-200">
                  <div className="flex items-center gap-2 pb-2 border-b border-indigo-300">
                    {getCategoryIcon('Screening Questions')}
                    <h3 className="text-lg font-semibold text-gray-900">Screening Questions</h3>
                  </div>
                  
                  {Object.entries(screeningQuestions).map(([jobId, questions]) => (
                    <div key={jobId} className="space-y-3">
                      {/* Job ID Header */}
                      <div className="flex items-center gap-2 text-sm text-indigo-700 font-medium bg-indigo-100 px-3 py-2 rounded-lg">
                        <Briefcase size={16} />
                        <span>Job: {jobId}</span>
                      </div>
                      
                      {/* Questions and Answers */}
                      <div className="space-y-3">
                        {Array.isArray(questions) && questions.map((qa, index) => (
                          <div key={index} className="bg-white p-4 rounded-lg border border-indigo-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {index + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 mb-3">
                                  {qa.question}
                                </p>
                                <div className="flex items-start gap-2 p-3 bg-indigo-50 rounded-lg">
                                  <CheckCircle size={18} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                                  <p className="text-gray-800 flex-1">
                                    {qa.answer || '-'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {/* Render regular fields by category */}
              {getCategories().map(category => {
                const categoryFields = getFieldsByCategory(category);
                if (categoryFields.length === 0) return null;
                
                return (
                  <section key={category} className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                      {getCategoryIcon(category)}
                      <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                      <span className="text-sm text-gray-500">({categoryFields.length})</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {categoryFields.map(field => {
                        const value = candidateData[field.fieldName];
                        const displayValue = formatValue(value, field.fieldName);
                        
                        return (
                          <div 
                            key={field.fieldName}
                            className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <p className="text-sm font-medium text-gray-600 mb-1">
                              {field.displayName || formatFieldName(field.fieldName)}
                            </p>
                            <p className="text-base font-semibold text-gray-900 break-words">
                              {displayValue}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

              {/* Technical Skills */}
              {candidateData.technicalSkills && candidateData.technicalSkills.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Award size={20} className="text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Technical Skills</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {candidateData.technicalSkills.map((skill, index) => (
                      <div 
                        key={index} 
                        className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center gap-2"
                      >
                        <span>{skill.skillName}</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              size={12} 
                              className={i < skill.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Soft Skills */}
              {candidateData.softSkills && candidateData.softSkills.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Award size={20} className="text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Soft Skills</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {candidateData.softSkills.map((skill, index) => (
                      <div 
                        key={index} 
                        className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-2"
                      >
                        <span>{skill.skillName}</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              size={12} 
                              className={i < skill.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Languages */}
              {candidateData.languages && candidateData.languages.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Globe size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {candidateData.languages.map((lang, index) => (
                      <div key={index} className="p-4 bg-indigo-50 rounded-lg">
                        <p className="font-semibold text-indigo-900 mb-2">{lang.languageName}</p>
                        <div className="flex gap-2">
                          {lang.canRead && <span className="px-2 py-1 bg-indigo-200 text-indigo-800 rounded text-xs">Read</span>}
                          {lang.canWrite && <span className="px-2 py-1 bg-indigo-200 text-indigo-800 rounded text-xs">Write</span>}
                          {lang.canSpeak && <span className="px-2 py-1 bg-indigo-200 text-indigo-800 rounded text-xs">Speak</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Preferred Locations */}
              {candidateData.preferredLocations && candidateData.preferredLocations.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <MapPin size={20} className="text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Preferred Locations</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {candidateData.preferredLocations.map((location, index) => (
                      <span 
                        key={index} 
                        className="px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                      >
                        {location}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Key Achievements */}
              {candidateData.keyAchievements && candidateData.keyAchievements.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Award size={20} className="text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Key Achievements</h3>
                  </div>
                  <ul className="space-y-2">
                    {candidateData.keyAchievements.map((achievement, index) => (
                      <li key={index} className="flex items-start gap-2 p-3 bg-purple-50 rounded-lg">
                        <span className="text-purple-600 font-bold">•</span>
                        <span className="text-gray-800">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Awards & Recognition */}
              {candidateData.awardsRecognition && candidateData.awardsRecognition.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Award size={20} className="text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Awards & Recognition</h3>
                  </div>
                  <div className="space-y-2">
                    {candidateData.awardsRecognition.map((award, index) => (
                      <div key={index} className="p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                        <p className="text-gray-800 font-medium">{award}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Status Reason */}
              {candidateData.reason && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <AlertCircle size={20} className="text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Status Reason</h3>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                    <p className="text-gray-800">{candidateData.reason}</p>
                  </div>
                </section>
              )}

              {/* Interview Slots */}
              {candidateData.timeslots && candidateData.timeslots.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <Clock size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Interview Slots</h3>
                  </div>
                  <div className="space-y-2">
                    {candidateData.timeslots.map((slot, index) => (
                      <div key={index} className="p-3 bg-indigo-50 rounded-lg border-l-4 border-indigo-500 flex items-center gap-3">
                        <span className="text-xs font-bold text-indigo-700 bg-indigo-100 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">{index + 1}</span>
                        <span className="text-gray-800 text-sm">{slot}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Pipeline Journey */}
              {candidateData.stageHistory && candidateData.stageHistory.length > 0 && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <CheckCircle size={20} className="text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Pipeline Journey</h3>
                  </div>
                  <div className="space-y-2">
                    {candidateData.stageHistory.map((entry, index) => (
                      <div key={index} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-blue-900">{entry.stage || 'Pipeline Exit'}</span>
                          {entry.changedAt && (
                            <span className="text-xs text-gray-500">
                              {new Date(entry.changedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">by {entry.changedBy || '—'}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button 
            onClick={onClose} 
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidatePreview;